import React, { useEffect, useMemo, useRef, useState } from 'react'
import { t } from 'ttag'
import { useHistory } from 'react-router'
import { useRouteMatch } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { type MRT_ColumnDef } from 'material-react-table'
import {
  Box,
  Typography,
  FormGroup,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core'
import YumCopyButton from '../../components/YumCopyButton/YumCopyButton'
import YumReadOnlyField from '../../components/YumReadOnlyField/YumReadOnlyField'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import YumButton from '../../components/YumButton/YumButton'
import YumInput from '../../components/YumInput/YumInput'
import YumFormSwitch from '../../components/YumFormSwitch/YumFormSwitch'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  CompanyDomain,
  GetCompanySsoConfig$Response,
  ScimConfigDto,
  SsoConfigListItem,
  SsoProtocol,
  UserCompanyDto,
  DeprovisionAction,
  ScimGroupMapping,
} from '../../services/api.types'
import { apiDomain } from '../../services/apiUrl'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumSelect from '../../components/YumSelect/YumSelect'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../components/YumDialogActions/YumDialogActions'
import YumDialogContentText from '../../components/YumDialogContentText/YumDialogContentText'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import YumTable from '../../components/YumTable/YumTable'
import YumTooltip from '../../components/YumTooltip/YumTooltip'
import DomainStatus from '../DomainAdmin/components/DomainStatus/DomainStatus'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'
import YumRemoveButton from '../../components/YumRemoveButton/YumRemoveButton'
import { ReactComponent as EditIcon } from '../../assets/v3/icons/dashboard/edit.svg'
import { hasCompanyFeatureFlag } from '../../common/helpers/featureFlags'

// --- Form data shape ---
interface SsoConfigFormData {
  name: string
  protocol: SsoProtocol
  jitProvisioningEnabled: boolean
  idpEntityId: string
  idpSsoUrl: string
  idpCertificate: string
  active: boolean
  linkedDomainIds: string[]
}

// --- Props ---
export interface ISsoAdminProps {
  managedCompanies: UserCompanyDto[]
  configs: SsoConfigListItem[]
  selectedConfig: GetCompanySsoConfig$Response | null
  loading: boolean
  saving: boolean
  deleting: boolean
  createdConfigId: string | null
  selectedCompanyId: string | null
  scimConfig: ScimConfigDto | null
  scimTokenVisible: string | null
  scimSaving: boolean
  domains: CompanyDomain[]
  ssoRequired: boolean
  ssoSettingsUpdating: boolean
}

export interface ISsoAdminDispatchProps {
  getCompanySsoConfigs: (payload: { companyId: string }) => void
  getCompanySsoConfig: (payload: { ssoConfigId: string }) => void
  createCompanySsoConfig: (payload: any) => void
  updateCompanySsoConfig: (payload: any) => void
  deleteCompanySsoConfig: (payload: { ssoConfigId: string; companyId: string }) => void
  setSsoSelectedCompanyId: (payload: string) => void
  enableScim: (payload: any) => void
  disableScim: (payload: { ssoConfigId: string }) => void
  regenerateScimToken: (payload: { ssoConfigId: string }) => void
  getCompanyDomains: (payload: { companyId: string }) => void
  getCompanySsoSettings: (payload: { companyId: string }) => void
  updateCompanySsoSettings: (payload: { companyId: string; ssoRequired: boolean }) => void
}

type SsoAdminProps = ISsoAdminProps & ISsoAdminDispatchProps

/**
 * Admin SSO Configuration page.
 *
 * Level 1: List of all SSO configs for a company.
 * Level 2: Detail/edit view for a single SSO config with domain linking and SCIM.
 */
export default function SsoAdmin(props: SsoAdminProps) {
  const {
    managedCompanies,
    configs,
    selectedConfig,
    loading,
    saving,
    deleting,
    createdConfigId,
    selectedCompanyId,
    scimConfig,
    scimTokenVisible,
    scimSaving,
    domains,
    ssoRequired,
    ssoSettingsUpdating,
    getCompanySsoConfigs,
    getCompanySsoConfig,
    createCompanySsoConfig,
    updateCompanySsoConfig,
    deleteCompanySsoConfig,
    setSsoSelectedCompanyId,
    enableScim,
    disableScim,
    regenerateScimToken,
    getCompanyDomains,
    getCompanySsoSettings,
    updateCompanySsoSettings,
  } = props

  const history = useHistory()

  // Determine view mode from URL
  const addMatch = useRouteMatch({ path: '/admin/sso/add', exact: true })
  const editMatch = useRouteMatch<{ configId: string }>({ path: '/admin/sso/:configId/edit', exact: true })
  const isCreatingNew = !!addMatch
  const editConfigId = editMatch?.params?.configId || null
  const isDetailView = isCreatingNew || !!editConfigId

  // ID of the config pending delete confirmation (used in both list and detail views)
  const [deleteConfigId, setDeleteConfigId] = useState<string | null>(null)

  // Wizard step for create flow (1=Name, 2=SP Metadata, 3=IdP Config)
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1)

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  const emptyFormValues: SsoConfigFormData = {
    name: '',
    protocol: SsoProtocol.SAML,
    jitProvisioningEnabled: false,
    idpEntityId: '',
    idpSsoUrl: '',
    idpCertificate: '',
    active: false,
    linkedDomainIds: [],
  }

  // SSO settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [localSsoRequired, setLocalSsoRequired] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SsoConfigFormData>({
    defaultValues: emptyFormValues,
  })

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSsoSelectedCompanyId(companyId)
    }
  }, [])

  // Load domains and SSO settings when selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanyDomains({ companyId })
      getCompanySsoSettings({ companyId })
    }
  }, [companyId])

  // Reload SSO configs list whenever list view is shown (handles post-create/delete refresh)
  useEffect(() => {
    if (companyId && !isDetailView) {
      getCompanySsoConfigs({ companyId })
    }
  }, [companyId, isDetailView])

  // Load config when navigating to the edit route
  useEffect(() => {
    if (editConfigId) {
      getCompanySsoConfig({ ssoConfigId: editConfigId })
    }
  }, [editConfigId])

  // Reset form and wizard step when entering the add route
  useEffect(() => {
    if (isCreatingNew) {
      reset(emptyFormValues)
      setDeleteConfigId(null)
      setWizardStep(1)
    }
  }, [isCreatingNew])

  // Auto-advance wizard from step 1 → 2 when config is created
  useEffect(() => {
    if (isCreatingNew && createdConfigId && wizardStep === 1) {
      setWizardStep(2)
    }
  }, [createdConfigId])

  // Populate form when selectedConfig loads (edit mode)
  useEffect(() => {
    if (selectedConfig && !isCreatingNew) {
      reset({
        name: selectedConfig.name || '',
        protocol: selectedConfig.protocol || SsoProtocol.SAML,
        jitProvisioningEnabled: selectedConfig.jitProvisioningEnabled || false,
        idpEntityId: selectedConfig.idpEntityId || '',
        idpSsoUrl: selectedConfig.idpSsoUrl || '',
        idpCertificate: selectedConfig.idpCertificate || '',
        active: selectedConfig.active || false,
        linkedDomainIds: selectedConfig.linkedDomainIds || [],
      })
    }
  }, [selectedConfig])

  // Handle company selection change
  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setSsoSelectedCompanyId(newCompanyId)
    history.push('/admin/sso')
  }

  // Navigate to config detail via URL
  const handleSelectConfig = (cfgId: string) => {
    history.push(`/admin/sso/${cfgId}/edit`)
  }

  // Navigate back to list via URL
  const handleBackToList = () => {
    setDeleteConfigId(null)
    history.push('/admin/sso')
  }

  // Navigate to add form via URL
  const handleCreateNew = () => {
    history.push('/admin/sso/add')
  }

  // Handle form submit (used for edit mode and wizard step 3)
  const onSubmit = (data: SsoConfigFormData) => {
    if (!companyId) return
    if (isCreatingNew && createdConfigId) {
      // Wizard step 3: update the config created in step 1
      updateCompanySsoConfig({ ssoConfigId: createdConfigId, companyId, ...data })
    } else if (selectedConfig?.configId) {
      updateCompanySsoConfig({ ssoConfigId: selectedConfig.configId, companyId, ...data })
    }
  }

  // Handle wizard step 1 submit (create with minimal data)
  const onWizardStep1Submit = (data: SsoConfigFormData) => {
    if (!companyId) return
    createCompanySsoConfig({
      companyId,
      name: data.name,
      protocol: SsoProtocol.SAML,
      active: false,
      jitProvisioningEnabled: false,
      linkedDomainIds: [],
    })
  }

  // Handle delete — works from both list and detail views
  const handleDelete = () => {
    if (!companyId || !deleteConfigId) return
    // Saga will navigate to /admin/sso on success
    deleteCompanySsoConfig({ ssoConfigId: deleteConfigId, companyId })
    setDeleteConfigId(null)
  }

  // SSO settings modal handlers
  const handleSettingsOpen = () => {
    setLocalSsoRequired(ssoRequired)
    setSettingsOpen(true)
  }

  const handleSettingsClose = () => {
    if (!ssoSettingsUpdating) setSettingsOpen(false)
  }

  const handleSettingsSave = () => {
    if (!companyId || ssoSettingsUpdating) return
    updateCompanySsoSettings({ companyId, ssoRequired: localSsoRequired })
  }

  // Auto-close settings dialog when update completes successfully
  const prevSettingsUpdating = useRef(false)
  useEffect(() => {
    if (prevSettingsUpdating.current && !ssoSettingsUpdating && settingsOpen) {
      setSettingsOpen(false)
    }
    prevSettingsUpdating.current = ssoSettingsUpdating
  }, [ssoSettingsUpdating])

  // Toggle domain in linkedDomainIds
  const toggleDomain = (domainId: string) => {
    const current = watch('linkedDomainIds') || []
    if (current.includes(domainId)) {
      setValue(
        'linkedDomainIds',
        current.filter((id: string) => id !== domainId),
      )
    } else {
      setValue('linkedDomainIds', [...current, domainId])
    }
  }

  // Verified domains for this company (backend returns uppercase status like 'VERIFIED')
  const verifiedDomains = (domains || []).filter((d) => d.status?.toLowerCase() === 'verified')

  // Table column definitions for the SSO configs list
  const ssoColumns = useMemo<MRT_ColumnDef<SsoConfigListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t`Name`,
        Cell: ({ row: { original } }: any) => (
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {original.name || t`Untitled`}
          </Typography>
        ),
      },
      {
        accessorKey: 'active',
        header: t`Status`,
        size: 120,
        Cell: ({ row: { original } }: any) => <DomainStatus status={original.active ? 'active' : 'inactive'} />,
      },
      {
        id: 'domains',
        header: t`Domains`,
        Cell: ({ row: { original } }: any) => (
          <Typography variant="body2">{(original.domains || []).join(', ') || '—'}</Typography>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        enableSorting: false,
        muiTableHeadCellProps: { align: 'right' },
        Cell: ({ row: { original } }: any) => (
          <Box
            style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', width: '100%' }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <YumTooltip title={t`Edit`} arrow placement="top">
              <IconButton onClick={() => handleSelectConfig(original.id)} style={{ borderRadius: '10px' }}>
                <EditIcon style={{ width: 20, height: 20 }} />
              </IconButton>
            </YumTooltip>
            <YumRemoveButton onClick={() => setDeleteConfigId(original.id)} />
          </Box>
        ),
      },
    ],
    [],
  )

  // SP Metadata URLs (use createdConfigId in wizard flow, selectedConfig in edit flow)
  const configId = isCreatingNew ? createdConfigId : selectedConfig?.configId
  const spEntityId = configId ? `${apiDomain}/saml/${configId}` : ''
  const acsUrl = configId ? `${apiDomain}/login/saml2/sso/${configId}` : ''
  const scimEndpointUrl = configId ? `${apiDomain}/scim/v2/${configId}` : ''

  // ──────────────────────────────────────────────────────────────────
  // RENDER: List view
  // ──────────────────────────────────────────────────────────────────
  if (!isDetailView) {
    return (
      <Card>
        <NotificationsWrapper />

        <YumHeader>
          <h1>
            {t`Single sign-on`}
            <span style={{ opacity: 0.5 }}> ({(configs || []).length})</span>
          </h1>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <YumButton variant="contained" color="primary" size="small" onClick={handleSettingsOpen}>
              {t`Settings`}
            </YumButton>
            {managedCompanies.length > 1 && (
              <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
                {managedCompanies.map((uc: UserCompanyDto, idx: number) => (
                  <option key={idx} value={uc.companyId}>
                    {uc.companyName}
                  </option>
                ))}
              </YumSmallSelect>
            )}
          </Box>
        </YumHeader>

        <YumTable
          enableRowNumbers={false}
          columns={ssoColumns as any}
          data={configs || []}
          getRowId={(row: any) => row.id as string}
          state={{ isLoading: loading }}
          initialState={{
            sorting: [{ id: 'name', desc: false }],
            density: 'compact',
            columnPinning: { right: ['actions'] },
          }}
          muiTableBodyRowProps={({ row }: any) => ({
            onClick: () => handleSelectConfig(row.original.id),
            style: { cursor: 'pointer' },
          })}
          renderTopToolbarCustomActions={() => (
            <YumButton color="secondary" variant="contained" size="small" onClick={handleCreateNew}>
              {t`Add new`}
            </YumButton>
          )}
        />

        {/* Delete confirmation dialog (shared) */}
        <YumDialog open={!!deleteConfigId}>
          <YumDialogTitle handleClose={() => setDeleteConfigId(null)}>{t`Delete SSO configuration`}</YumDialogTitle>
          <YumDialogContent>
            <YumDialogContentText>
              {t`Are you sure you want to delete this SSO configuration? Users will need to use password login instead.`}
            </YumDialogContentText>
          </YumDialogContent>
          <YumDialogActions>
            <YumCancelButton onClick={() => setDeleteConfigId(null)} disabled={deleting} />
            <YumSubmitButton text={t`Delete`} onClick={handleDelete} disabled={deleting} color="error" />
          </YumDialogActions>
        </YumDialog>

        {/* SSO settings dialog */}
        <YumDialog open={settingsOpen}>
          <YumDialogTitle handleClose={handleSettingsClose}>{t`SSO settings`}</YumDialogTitle>
          <YumDialogContent>
            <ListItem
              role={undefined}
              dense
              button
              onClick={() => setLocalSsoRequired(!localSsoRequired)}
              style={{ paddingLeft: 0 }}
            >
              <ListItemText
                primary={t`SSO required`}
                secondary={t`When enabled, users must use SSO to log in. Password login is blocked.`}
                secondaryTypographyProps={{ style: { fontSize: '14px', marginTop: '4px' } }}
              />
              <ListItemIcon style={{ minWidth: 'auto' }}>
                <YumSwitch checked={localSsoRequired} disabled={ssoSettingsUpdating} />
              </ListItemIcon>
            </ListItem>
          </YumDialogContent>
          <YumDialogActions>
            <YumCancelButton onClick={handleSettingsClose} submitInProgress={ssoSettingsUpdating} />
            <YumSubmitButton
              text={t`Save`}
              onClick={handleSettingsSave}
              submitInProgress={ssoSettingsUpdating}
              immersiveLoader={true}
            />
          </YumDialogActions>
        </YumDialog>
      </Card>
    )
  }

  // ──────────────────────────────────────────────────────────────────
  // Shared sections (used in both edit and wizard step 3)
  // ──────────────────────────────────────────────────────────────────
  const renderIdpFields = () => (
    <>
      <Typography variant="h2" style={{ marginTop: 24, marginBottom: 12 }}>
        {t`Identity provider (IdP) configuration`}
      </Typography>
      <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
        {t`Enter the SAML settings provided by your identity provider (e.g., Okta, Azure AD, OneLogin).`}
      </Typography>

      <Controller
        name="idpSsoUrl"
        control={control}
        render={({ field }) => (
          <YumInput
            label={t`SSO URL / ACS URL / Reply URL`}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder="https://idp.example.com/sso/saml"
            style={{ marginBottom: errors.idpSsoUrl ? 0 : 12 }}
          />
        )}
      />
      {errors.idpSsoUrl && <ErrorMessage>{errors.idpSsoUrl.message}</ErrorMessage>}

      <Controller
        name="idpEntityId"
        control={control}
        render={({ field }) => (
          <YumInput
            label={t`Entity ID / Issuer / Identifier`}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder="https://idp.example.com/entity"
            style={{ marginBottom: errors.idpEntityId ? 0 : 12 }}
          />
        )}
      />
      {errors.idpEntityId && <ErrorMessage>{errors.idpEntityId.message}</ErrorMessage>}

      <Controller
        name="idpCertificate"
        control={control}
        render={({ field }) => (
          <YumInput
            label={t`Certificate (PEM format)`}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t`Paste the IdP certificate here...`}
            multiline
            rows={4}
            style={{ marginBottom: errors.idpCertificate ? 0 : 12 }}
          />
        )}
      />
      {errors.idpCertificate && <ErrorMessage>{errors.idpCertificate.message}</ErrorMessage>}
    </>
  )

  const renderLinkedDomains = () => (
    <>
      <Typography variant="h2" style={{ marginTop: 24, marginBottom: 12 }}>
        {t`Linked domains`}
      </Typography>
      <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
        {t`Select which verified domains should route to this SSO configuration. Users with emails matching these domains will be directed to this identity provider.`}
      </Typography>

      {verifiedDomains.length > 0 ? (
        <Box style={{ marginBottom: 16 }}>
          {verifiedDomains.map((domain) => {
            const linkedIds = watch('linkedDomainIds') || []
            const isLinked = linkedIds.includes(domain.id)
            const linkedToOther =
              !isLinked && domain.ssoConfigId && domain.ssoConfigId !== (configId || selectedConfig?.configId)

            return (
              <FormControlLabel
                key={domain.id}
                control={
                  <Checkbox
                    checked={isLinked}
                    onChange={() => toggleDomain(domain.id)}
                    disabled={!!linkedToOther}
                    color="primary"
                  />
                }
                label={
                  <span>
                    {domain.domain}
                    {linkedToOther && (
                      <Typography component="span" variant="caption" style={{ marginLeft: 8, color: '#999' }}>
                        {t`(linked to another config)`}
                      </Typography>
                    )}
                  </span>
                }
                style={{ display: 'block', marginBottom: 4 }}
              />
            )
          })}
        </Box>
      ) : (
        <Typography variant="body2" style={{ marginBottom: 16, color: '#999' }}>
          {t`No verified domains found. Add and verify domains in the Domain Management page first.`}
        </Typography>
      )}
    </>
  )

  const renderSettingsToggles = () => (
    <>
      <Typography variant="h2" style={{ marginTop: 24, marginBottom: 12 }}>
        {t`Settings`}
      </Typography>

      <FormGroup>
        <ListItem
          role={undefined}
          dense
          button
          style={{ paddingLeft: 0 }}
          onClick={() => setValue('active', !watch('active'))}
        >
          <ListItemText
            primary={t`SSO active`}
            secondary={t`Enable SSO login for this connection`}
            secondaryTypographyProps={{ style: { fontSize: '14px', marginTop: '4px' } }}
          />
          <ListItemIcon style={{ minWidth: 'auto' }}>
            <YumFormSwitch name="active" control={control} />
          </ListItemIcon>
        </ListItem>

        <ListItem
          role={undefined}
          dense
          button
          style={{ paddingLeft: 0 }}
          onClick={() => setValue('jitProvisioningEnabled', !watch('jitProvisioningEnabled'))}
        >
          <ListItemText
            primary={t`JIT provisioning`}
            secondary={t`Automatically create new user accounts on first SSO login`}
            secondaryTypographyProps={{ style: { fontSize: '14px', marginTop: '4px' } }}
          />
          <ListItemIcon style={{ minWidth: 'auto' }}>
            <YumFormSwitch name="jitProvisioningEnabled" control={control} />
          </ListItemIcon>
        </ListItem>
      </FormGroup>
    </>
  )

  const renderSpMetadata = () => (
    <Box style={{ marginTop: 24 }}>
      <Typography variant="h2" style={{ marginBottom: 12 }}>
        {t`Service provider (SP) metadata`}
      </Typography>
      <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
        {t`Share these values with your IdP administrator to complete the SSO setup.`}
      </Typography>

      <YumReadOnlyField label={t`SSO URL / ACS URL / Reply URL`} value={acsUrl} />
      <YumReadOnlyField label={t`Entity ID / Issuer / Identifier`} value={spEntityId} />

      <Typography variant="body2" style={{ fontWeight: 600, marginTop: 8, marginBottom: 4 }}>
        {t`Following attribute mappings must be set up at Identity Provider:`}
      </Typography>
      <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
        <li>
          <Typography variant="body2">email</Typography>
        </li>
        <li>
          <Typography variant="body2">first_name</Typography>
        </li>
        <li>
          <Typography variant="body2">last_name</Typography>
        </li>
      </ul>
    </Box>
  )

  // ──────────────────────────────────────────────────────────────────
  // RENDER: Create wizard flow
  // ──────────────────────────────────────────────────────────────────
  if (isCreatingNew) {
    return (
      <Card>
        <NotificationsWrapper />

        <YumHeader>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.5rem' }}>
            <IconButton size="small" onClick={handleBackToList}>
              <ArrowBackIcon />
            </IconButton>
            <h1 style={{ margin: 0 }}>
              {t`New configuration`}
              <Typography component="span" variant="body2" style={{ marginLeft: 12, color: '#999' }}>
                {t`Step`} {wizardStep} / 3
              </Typography>
            </h1>
          </Box>
        </YumHeader>

        {/* --- Wizard Step 1: Name --- */}
        {wizardStep === 1 && (
          <form onSubmit={handleSubmit(onWizardStep1Submit)}>
            <Controller
              name="name"
              control={control}
              rules={{ required: t`Configuration name is required.` }}
              render={({ field }) => (
                <YumInput
                  label={t`Configuration name`}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t`e.g., US Office (Azure AD)`}
                  style={{ marginBottom: errors.name ? 0 : 12 }}
                />
              )}
            />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

            <Box style={{ marginTop: 24 }}>
              <YumButton type="submit" showLoader={true} disabled={saving || !companyId}>
                {t`Start configuring`}
              </YumButton>
            </Box>
          </form>
        )}

        {/* --- Wizard Step 2: SP Metadata --- */}
        {wizardStep === 2 && configId && (
          <>
            {renderSpMetadata()}

            <Box style={{ marginTop: 24 }}>
              <YumButton onClick={() => setWizardStep(3)}>{t`Continue`}</YumButton>
            </Box>
          </>
        )}

        {/* --- Wizard Step 3: IdP Configuration --- */}
        {wizardStep === 3 && (
          <form onSubmit={handleSubmit(onSubmit)}>
            {renderIdpFields()}
            {renderLinkedDomains()}
            {renderSettingsToggles()}

            <Box style={{ marginTop: 24 }}>
              <YumButton type="submit" showLoader={true} disabled={saving || !companyId}>
                {t`Save`}
              </YumButton>
            </Box>
          </form>
        )}
      </Card>
    )
  }

  // ──────────────────────────────────────────────────────────────────
  // RENDER: Edit view (existing config — single form, unchanged)
  // ──────────────────────────────────────────────────────────────────
  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.5rem' }}>
          <IconButton size="small" onClick={handleBackToList}>
            <ArrowBackIcon />
          </IconButton>
          <h1 style={{ margin: 0 }}>{selectedConfig?.name || t`Configuration`}</h1>
        </Box>
      </YumHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* --- Configuration Name --- */}
        <Controller
          name="name"
          control={control}
          rules={{ required: t`Configuration name is required.` }}
          render={({ field }) => (
            <YumInput
              label={t`Configuration name`}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t`e.g., US Office (Azure AD)`}
              style={{ marginBottom: errors.name ? 0 : 12 }}
            />
          )}
        />
        {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

        {renderIdpFields()}
        {renderLinkedDomains()}
        {renderSettingsToggles()}

        {/* --- SP Metadata Section --- */}
        {configId && renderSpMetadata()}

        {/* --- SCIM Provisioning Section --- */}
        {configId && hasCompanyFeatureFlag(managedCompanies, companyId, 'scimAdmin') && (
          <ScimSection
            configId={configId}
            scimConfig={scimConfig}
            scimTokenVisible={scimTokenVisible}
            scimSaving={scimSaving}
            scimEndpointUrl={scimEndpointUrl}
            enableScim={enableScim}
            disableScim={disableScim}
            regenerateScimToken={regenerateScimToken}
          />
        )}

        {/* --- Action Buttons --- */}
        <Box style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          {selectedConfig && (
            <YumButton
              variant="outlined"
              onClick={() => setDeleteConfigId(selectedConfig.configId || null)}
              disabled={deleting}
              style={{ color: '#f44336', borderColor: '#f44336' }}
            >
              {t`Delete`}
            </YumButton>
          )}

          <YumButton type="submit" showLoader={true} disabled={saving || !companyId}>
            {t`Save`}
          </YumButton>
        </Box>
      </form>

      {/* Delete confirmation dialog */}
      <YumDialog open={!!deleteConfigId}>
        <YumDialogTitle handleClose={() => setDeleteConfigId(null)}>{t`Delete SSO configuration`}</YumDialogTitle>
        <YumDialogContent>
          <YumDialogContentText>
            {t`Are you sure you want to delete this SSO configuration? Users will need to use password login instead.`}
          </YumDialogContentText>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={() => setDeleteConfigId(null)} disabled={deleting} />
          <YumSubmitButton text={t`Delete`} onClick={handleDelete} disabled={deleting} color="error" />
        </YumDialogActions>
      </YumDialog>
    </Card>
  )
}

// ──────────────────────────────────────────────────────────────────
// SCIM Provisioning section
// ──────────────────────────────────────────────────────────────────
function ScimSection({
  configId,
  scimConfig,
  scimTokenVisible,
  scimSaving,
  scimEndpointUrl,
  enableScim,
  disableScim,
  regenerateScimToken,
}: {
  configId: string
  scimConfig: ScimConfigDto | null
  scimTokenVisible: string | null
  scimSaving: boolean
  scimEndpointUrl: string
  enableScim: (payload: any) => void
  disableScim: (payload: { ssoConfigId: string }) => void
  regenerateScimToken: (payload: { ssoConfigId: string }) => void
}) {
  const [deprovisionAction, setDeprovisionAction] = useState<DeprovisionAction>('DEACTIVATE')
  const [groupMapping, setGroupMapping] = useState<ScimGroupMapping>('NONE')

  // Sync local state with loaded scimConfig
  useEffect(() => {
    if (scimConfig) {
      setDeprovisionAction(scimConfig.deprovisionAction || 'DEACTIVATE')
      setGroupMapping(scimConfig.groupMapping || 'NONE')
    }
  }, [scimConfig])

  const isEnabled = scimConfig !== null

  // Toggle SCIM on/off via the switch
  const handleToggleScim = () => {
    if (isEnabled) {
      disableScim({ ssoConfigId: configId })
    } else {
      enableScim({
        ssoConfigId: configId,
        deprovisionAction,
        groupMapping,
      })
    }
  }

  // Update SCIM settings when changed while already enabled
  const handleDeprovisionChange = (e: any) => {
    const value = e.target.value as DeprovisionAction
    setDeprovisionAction(value)
    if (isEnabled) {
      enableScim({ ssoConfigId: configId, deprovisionAction: value, groupMapping })
    }
  }

  const handleGroupMappingChange = (e: any) => {
    const value = e.target.value as ScimGroupMapping
    setGroupMapping(value)
    if (isEnabled) {
      enableScim({ ssoConfigId: configId, deprovisionAction, groupMapping: value })
    }
  }

  const handleRegenerateToken = () => {
    regenerateScimToken({ ssoConfigId: configId })
  }

  return (
    <Box style={{ marginTop: 24 }}>
      <Typography variant="h2" style={{ marginBottom: 12 }}>
        {t`SCIM provisioning`}
      </Typography>
      <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
        {t`Automatically sync users from your identity provider using SCIM.`}
      </Typography>

      {/* Enable/disable SCIM toggle */}
      <ListItem role={undefined} dense button style={{ paddingLeft: 0 }} onClick={handleToggleScim}>
        <ListItemText
          primary={t`SCIM enabled`}
          secondary={t`Sync users and groups from your identity provider`}
          secondaryTypographyProps={{ style: { fontSize: '14px', marginTop: '4px' } }}
        />
        <ListItemIcon style={{ minWidth: 'auto' }}>
          <YumSwitch checked={isEnabled} disabled={scimSaving} />
        </ListItemIcon>
      </ListItem>

      {/* Deprovision action + group sync — always editable */}
      <Box style={{ marginTop: 16 }}>
        <Box style={{ marginBottom: 16 }}>
          <YumSelect label={t`Deprovision action`} value={deprovisionAction} onChange={handleDeprovisionChange}>
            <option value="DEACTIVATE">{t`Deactivate user (preserve data)`}</option>
            <option value="DELETE">{t`Remove user from company`}</option>
          </YumSelect>
        </Box>

        <Box style={{ marginBottom: 16 }}>
          <YumSelect label={t`Group sync`} value={groupMapping} onChange={handleGroupMappingChange}>
            <option value="NONE">{t`Don't sync groups`}</option>
            <option value="DIVISIONS">{t`Sync to divisions`}</option>
            <option value="TEAMS">{t`Sync to teams`}</option>
          </YumSelect>
        </Box>
      </Box>

      {/* Enabled: show SCIM endpoint, token, last sync, and actions */}
      {isEnabled && (
        <Box style={{ marginTop: 8 }}>
          {/* SCIM Endpoint URL */}
          <YumReadOnlyField label={t`SCIM endpoint`} value={scimEndpointUrl} />

          {/* Bearer token (shown once after enable/regenerate) */}
          {scimTokenVisible && (
            <Box
              style={{
                padding: 16,
                marginBottom: 16,
                backgroundColor: '#e8f5e9',
                borderRadius: 8,
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {t`Bearer token (copy now - it won't be shown again)`}
              </Typography>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#fff',
                  borderRadius: 4,
                  padding: '8px 12px',
                }}
              >
                <Typography
                  variant="body2"
                  style={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 13 }}
                >
                  {scimTokenVisible}
                </Typography>
                <YumCopyButton text={scimTokenVisible} />
              </Box>
            </Box>
          )}

          {scimConfig.lastSyncAt && (
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              <strong>{t`Last sync`}:</strong> {new Date(scimConfig.lastSyncAt).toLocaleString()}
            </Typography>
          )}

          {/* Regenerate token button */}
          <YumButton variant="outlined" onClick={handleRegenerateToken} disabled={scimSaving}>
            {t`Regenerate token`}
          </YumButton>
        </Box>
      )}
    </Box>
  )
}
